import { useEffect, useState } from "react";
import AdminLayout from "./components/AdminLayout";
import { supabase } from "@/lib/supabase";
import type { Banner } from "@/lib/supabase";

export default function BannersAdminPage() {
  const [items, setItems] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Partial<Banner> | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    const { data } = await supabase.from("banners").select("*").order("display_order");
    setItems(data ?? []);
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const openNew = () => { setEditing({ title: "", subtitle: "", cta_label: "", cta_link: "", active: true }); setShowModal(true); };
  const openEdit = (b: Banner) => { setEditing(b); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setEditing(null); };

  const handleSave = async () => {
    if (!editing?.title) return;
    setSaving(true);
    if (editing.id) {
      await supabase.from("banners").update({ title: editing.title, subtitle: editing.subtitle, image_url: editing.image_url, cta_label: editing.cta_label, cta_link: editing.cta_link, active: editing.active }).eq("id", editing.id);
    } else {
      await supabase.from("banners").insert({ title: editing.title, subtitle: editing.subtitle, image_url: editing.image_url, cta_label: editing.cta_label, cta_link: editing.cta_link, active: editing.active ?? true });
    }
    setSaving(false);
    closeModal();
    fetchData();
  };

  const handleDelete = async (id: string) => {
    await supabase.from("banners").delete().eq("id", id);
    setDeleteId(null);
    fetchData();
  };

  const toggleActive = async (b: Banner) => {
    await supabase.from("banners").update({ active: !b.active }).eq("id", b.id);
    fetchData();
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1a1a1a]">Banners</h1>
          <p className="text-gray-500 text-sm mt-1">{items.length} banner(s)</p>
        </div>
        <button onClick={openNew} className="flex items-center gap-2 bg-[#1B1F5E] hover:bg-[#2F4F8F] text-white font-bold text-sm px-4 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line"></i></div>
          Novo Banner
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {loading ? (
          <div className="col-span-2 flex items-center justify-center h-40"><div className="w-6 h-6 flex items-center justify-center text-[#1B1F5E]"><i className="ri-loader-4-line animate-spin text-2xl"></i></div></div>
        ) : items.length === 0 ? (
          <div className="col-span-2 text-center py-16 text-gray-400 bg-white rounded-xl border border-gray-200">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3"><i className="ri-image-line text-4xl"></i></div>
            <p className="text-sm">Nenhum banner cadastrado</p>
          </div>
        ) : items.map(b => (
          <div key={b.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {b.image_url ? (
              <div className="h-36 overflow-hidden">
                <img src={b.image_url} alt={b.title} className="w-full h-full object-cover object-top" />
              </div>
            ) : (
              <div className="h-36 bg-gray-100 flex items-center justify-center">
                <i className="ri-image-line text-gray-300 text-4xl"></i>
              </div>
            )}
            <div className="p-4">
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-sm font-bold text-[#1a1a1a]">{b.title}</h3>
                <button onClick={() => toggleActive(b)} className={`w-9 h-5 rounded-full transition-colors cursor-pointer relative flex-shrink-0 ml-2 ${b.active ? "bg-[#1B1F5E]" : "bg-gray-200"}`}>
                  <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${b.active ? "left-4" : "left-0.5"}`}></span>
                </button>
              </div>
              {b.subtitle && <p className="text-xs text-gray-500 mb-3">{b.subtitle}</p>}
              {b.cta_label && <span className="inline-block bg-[#1B1F5E]/10 text-[#1B1F5E] text-xs font-medium px-2 py-0.5 rounded-full mb-3">{b.cta_label}</span>}
              <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                <button onClick={() => openEdit(b)} className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-gray-600 hover:text-[#1B1F5E] transition-colors cursor-pointer">
                  <i className="ri-edit-line"></i> Editar
                </button>
                <button onClick={() => setDeleteId(b.id)} className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-gray-600 hover:text-red-500 transition-colors cursor-pointer">
                  <i className="ri-delete-bin-line"></i> Excluir
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showModal && editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-[#1a1a1a]">{editing.id ? "Editar Banner" : "Novo Banner"}</h2>
              <button onClick={closeModal} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-xl"></i></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#333] mb-1.5">Título *</label>
                <input value={editing.title ?? ""} onChange={e => setEditing({ ...editing, title: e.target.value })} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#1B1F5E]" placeholder="Título do banner" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#333] mb-1.5">Subtítulo</label>
                <input value={editing.subtitle ?? ""} onChange={e => setEditing({ ...editing, subtitle: e.target.value })} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#1B1F5E]" placeholder="Subtítulo" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#333] mb-1.5">URL da Imagem</label>
                <input value={editing.image_url ?? ""} onChange={e => setEditing({ ...editing, image_url: e.target.value })} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#1B1F5E]" placeholder="https://..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#333] mb-1.5">Texto do Botão</label>
                  <input value={editing.cta_label ?? ""} onChange={e => setEditing({ ...editing, cta_label: e.target.value })} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#1B1F5E]" placeholder="Ex: Saiba mais" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#333] mb-1.5">Link do Botão</label>
                  <input value={editing.cta_link ?? ""} onChange={e => setEditing({ ...editing, cta_link: e.target.value })} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#1B1F5E]" placeholder="/contato" />
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={editing.active ?? true} onChange={e => setEditing({ ...editing, active: e.target.checked })} className="w-4 h-4 accent-[#1B1F5E]" />
                <span className="text-sm text-[#333]">Ativo no site</span>
              </label>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={closeModal} className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">Cancelar</button>
              <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 bg-[#1B1F5E] hover:bg-[#2F4F8F] text-white font-bold text-sm rounded-lg cursor-pointer whitespace-nowrap disabled:opacity-60">{saving ? "Salvando..." : "Salvar"}</button>
            </div>
          </div>
        </div>
      )}

      {deleteId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl w-full max-w-sm p-6 text-center">
            <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4"><i className="ri-delete-bin-line text-red-500 text-xl"></i></div>
            <h3 className="text-base font-bold text-[#1a1a1a] mb-2">Excluir banner?</h3>
            <p className="text-gray-500 text-sm mb-6">Esta ação não pode ser desfeita.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap">Cancelar</button>
              <button onClick={() => handleDelete(deleteId)} className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white font-bold text-sm rounded-lg cursor-pointer whitespace-nowrap">Excluir</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
